import { Box, SvgIcon, SvgIconProps, Typography } from '@mui/material';
import {
  AlertCircle as AlertCircleIcon,
  Alert as AlertIcon,
  CheckCircle as CheckCircleIcon,
  Information as InfoIcon
} from '@opengov/react-capital-assets';
import React from 'react';

import { CloseButton } from '../Components/CloseButton';
import {
  CAPITAL_DESIGN_TOKENS,
  CAPITAL_FOUNDATION_COLORS,
  NAV_COLOR_PALETTE,
  SUBMENU_BOX_SHADOW
} from '../Components/styleConstants';
import { DATA_TEST, getDynamicDataTestId } from '../enums';

import { IToastContent, ToastVariant } from './types';

interface IToastProps {
  toast: IToastContent;
  onClose(): void;
}

export const Toast: React.FC<IToastProps> = ({ toast, onClose }) => {
  const { title, body, variant } = toast;

  const variantTokens: {
    [key in ToastVariant]: {
      icon: React.ComponentType<SvgIconProps>;
      iconColor: string;
      backgroundColor: string;
    };
  } = {
    [ToastVariant.SUCCESS]: {
      icon: CheckCircleIcon,
      iconColor: CAPITAL_FOUNDATION_COLORS.green50,
      backgroundColor: CAPITAL_FOUNDATION_COLORS.green700
    },
    [ToastVariant.ERROR]: {
      icon: AlertCircleIcon,
      iconColor: CAPITAL_FOUNDATION_COLORS.red50,
      backgroundColor: CAPITAL_FOUNDATION_COLORS.red700
    },
    [ToastVariant.WARNING]: {
      icon: AlertIcon,
      iconColor: CAPITAL_FOUNDATION_COLORS.yellow50,
      backgroundColor: CAPITAL_FOUNDATION_COLORS.yellow700
    },
    [ToastVariant.INFO]: {
      icon: InfoIcon,
      iconColor: CAPITAL_FOUNDATION_COLORS.white,
      backgroundColor: CAPITAL_FOUNDATION_COLORS.blurple700
    }
  };

  const ToastIcon = variantTokens[variant].icon;

  return (
    <Box
      sx={{
        boxShadow: SUBMENU_BOX_SHADOW,
        p: 2,
        borderRadius: 1,
        backgroundColor: variantTokens[variant].backgroundColor,
        width: '100%',
        maxWidth: 520
      }}
      className="toastContainer"
      data-test={toast.dataTest}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
        <SvgIcon
          inheritViewBox
          component={ToastIcon}
          fontSize="small"
          sx={{ color: variantTokens[variant].iconColor, mt: 1 }}
          className="toastIcon"
          data-test={getDynamicDataTestId(DATA_TEST.toast_icon, variant)}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', pb: 1, ml: 1, color: variantTokens[variant].iconColor }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h4" component="span" className="toastTitle">
              {title}
            </Typography>
            <CloseButton
              onClick={onClose}
              dataTest={DATA_TEST.toast_close_button}
              styleOverrides={{
                color: CAPITAL_FOUNDATION_COLORS.white,
                borderRadius: '50%',
                '&.Mui-focusVisible': {
                  color: NAV_COLOR_PALETTE.secondaryGray,
                  backgroundColor: CAPITAL_DESIGN_TOKENS.semanticColors.background.tertiary
                }
              }}
            />
          </Box>
          <Typography variant="body1" component="span" className="toastBody">
            {body}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
